import { Fragment, useState, useRef, useEffect } from "react";
import { flushSync } from "react-dom";
import {
  ViewerApi,
  RendererTypes,
  ENVIRONMENTS,
  Link,
  Media,
  Image,
  Screen,
  ViewerConfig,
  Route,
  Ancestry,
  IAnnotationPopoverData,
  ScrollData,
  PageData,
  ThemeVariant,
} from "@bronte/viewer-api/src/main";
import { PageNumbers } from "@bronte/common";

import { ViewerExpressLegacyAdapter } from "../../viewer-express-api/src/adapters/ViewerExpressLegacyAdapter";
import { getPageNumbers } from "./components/NavigationBar/utils";

import {
  getURLParams,
  getAuthToken,
  getSessionJsURL,
  getClientId,
  getTitle,
  getUniqueIdForAnnotation,
  isLocalhost,
  getDevShellExpressGatewayBaseUrl,
  syncScreenIdUrlParam,
} from "./shared/utils";
import { loadSessionJs, requirePiLogin } from "./shared/piLogin";
import { DataProvider } from "./shared/DataProvider";
import { IAnnotationData, SpotlightAudio } from "./shared/types";
import {
  isLessonAudioFailed,
  NO_AUDIO_FOR_SCREEN_MESSAGE,
  probeLocalCourseAudio,
  getLocalAudioBaseUrl,
} from "./shared/localCourseAudio";
import { isOldCourseForExpress, OLD_COURSE_EXPRESS_MESSAGE } from "./shared/expressCourseVersion";
import { getShellPackageDates, ShellPackageDates } from "./shared/buildDates";
import { buildLocalSpotlightAudio } from "./shared/spotlightAudioConfig";
import { AnnotationActions, AnnotationTypes, DefaultThemeOptions } from "./shared/constants";
import { getThemeVariantId } from "./shared/themeVariants";
import {
  DEFAULT_DEV_SHELL_DEVICE_TYPE,
  readStoredDeviceType,
  writeStoredDeviceType,
  getExpressShellTitle,
  shouldShowDeviceTypeToggle,
  type DevShellDeviceType,
} from "./shared/deviceType";
import { loadTestEtextWidget, closeTestEtextWidget } from "./shared/loadTestEtextWidget";
import type { EtextWidgetTestConfig } from "./shared/etextWidgetTestConfig";

import { getCoursewareStructure } from "./components/Toc";
import { resolveInitialRoute, InitialRoute } from "./components/Toc/resolveInitialRoute";
import AsideBar from "./components/AsideBar";
import { AppHeader } from "./components/AppHeader";
import { EnvironmentForm } from "./components/EnvironmentForm";
import { Annotations } from "./components/Annotations";
import { ThemeContext } from "./shared/ThemeContext";
import { NavigationBar, NavigationBarHandle } from "./components/NavigationBar";
import { IViewerApi } from "@bronte/viewer-api/src/IViewerApi";

let viewerApi: IViewerApi;
let annotationData: IAnnotationData[] = [];
let currentRenderedId = "";
const isAndroid = navigator.userAgent.match(/Android/i);

const App = () => {
  const urlParams = getURLParams(process.env.VITE_BV_RENDERER);
  const renderer = process.env.VITE_BV_RENDERER as RendererTypes;
  const isExpressBuild = renderer === RendererTypes.EXPRESS;
  const [packagePath] = useState(urlParams.packagePath || process.env.VITE_PACKAGE_DIR);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [packageWarning, setPackageWarning] = useState("");
  const [dates, setDates] = useState<ShellPackageDates>({});
  const frameElement = useRef(document.createElement("div"));
  const [tocNodeHtml, setTocNodeHtml] = useState("");
  const [screensState, setScreensState] = useState<Screen[]>([]);
  const [renderedId, setRenderedId] = useState("");
  const [showAnnotationMenu, setShowAnnotationMenu] = useState(false);
  const [spotlightAudio, setSpotlightAudio] = useState<SpotlightAudio>();
  const [localCourseAudioEnabled, setLocalCourseAudioEnabled] = useState(false);
  const [failedScreenIds, setFailedScreenIds] = useState<Set<string>>(() => new Set());
  // Set once init() reaches loadPage: a hosted secure package's CloudFront cookies exist from then on
  const [packageAccessReady, setPackageAccessReady] = useState(false);
  const [currentAnnotation, setCurrentAnnotation] = useState<IAnnotationData>(
    {} as IAnnotationData,
  );
  const [showAnnotationsPopover, setShowAnnotationsPopover] = useState(false);
  const [annotationsPopoverData, setAnnotationsPopoverData] = useState<IAnnotationPopoverData>(
    {} as IAnnotationPopoverData,
  );
  const [prevItem, setPrevItem] = useState<Screen>();
  const [nextItem, setNextItem] = useState<Screen>();
  const [imageData, setImageData] = useState<Image | null>(null);
  const [currentTheme, setCurrentTheme] = useState(DefaultThemeOptions);
  const [themeVariants, setThemeVariants] = useState<ThemeVariant[]>([]);
  const [routeCompleted, setRouteCompleted] = useState(false);
  const [annotationUpdated, setAnnotationUpdated] = useState(false);
  const [notesPopupDisplay, setNotesPopupDisplay] = useState(false);
  const [flashcardPopupDisplay, setFlashcardPopupDisplay] = useState(false);
  const [pageNumber, setPageNumber] = useState("-");
  const [stitchedLessonRenderId, setStitchedLessonRenderId] = useState("");
  const [parentActivityId, setParentActivityId] = useState<string | undefined>(undefined);
  const pageNumbersMapRef = useRef<PageNumbers>({});
  const [isAudioPlayerVisible, setAudioPlayerVisible] = useState(false);
  const showLoaderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const piTokenRef = useRef<string | null>(null);
  const navigationBarRef = useRef<NavigationBarHandle>(null);

  const isOfflinePackage = !!packagePath;

  const usesHostedPiAuth = urlParams.securePackageAccess || renderer === RendererTypes.EXPRESS;
  const isUatBuild = process.env.VITE_BV_UAT === "true";
  const isDeviceTypeToggleEnabled = shouldShowDeviceTypeToggle(
    isOfflinePackage,
    isExpressBuild,
    urlParams.minimalShell,
    isUatBuild,
  );
  const deviceType: DevShellDeviceType = isDeviceTypeToggleEnabled
    ? readStoredDeviceType()
    : DEFAULT_DEV_SHELL_DEVICE_TYPE;

  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;

    root.classList.remove(
      "renderer-web",
      "renderer-mobile",
      "renderer-web-uat",
      "renderer-express-uat",
    );

    if (renderer === RendererTypes.WEB) {
      root.classList.add(isUatBuild ? "renderer-web-uat" : "renderer-web");
    } else if (renderer === RendererTypes.MOBILE) {
      root.classList.add("renderer-mobile");
    } else if (renderer === RendererTypes.EXPRESS && isUatBuild) {
      root.classList.add("renderer-express-uat");
    }
  }, [renderer, isUatBuild]);

  const getOfflineShellMessage = (activeDeviceType: DevShellDeviceType = deviceType): string => {
    if (renderer === RendererTypes.EXPRESS) {
      return getExpressShellTitle(activeDeviceType, isDeviceTypeToggleEnabled, isUatBuild);
    }
    if (renderer === RendererTypes.MOBILE) {
      return "BV mobile";
    }
    if (renderer === RendererTypes.WEB) {
      return isUatBuild ? "BV UAT" : "BV Web";
    }
    return "";
  };

  const handleDeviceTypeChange = (nextDeviceType: DevShellDeviceType): void => {
    if (!isDeviceTypeToggleEnabled || nextDeviceType === deviceType) {
      return;
    }
    writeStoredDeviceType(nextDeviceType);
    window.location.reload();
  };

  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;

    root.classList.remove("theme-sepia", "theme-night");

    const activeVariant = themeVariants.find(
      (variant) => variant.variantId === currentTheme.variantId,
    );
    const themeVariantId = getThemeVariantId(activeVariant?.variantName);

    if (themeVariantId !== "main") {
      root.classList.add(`theme-${themeVariantId}`);
    }
  }, [currentTheme.variantId, themeVariants]);

  const getPrevNextItem = (screens: Screen[], currentId: string): void => {
    const i = screens.findIndex((screen) => screen.elementId === currentId);
    if (i < 0) {
      setNextItem(undefined);
      setPrevItem(undefined);
      return;
    }
    setNextItem(screens[i + 1]);
    setPrevItem(screens[i - 1]);
  };

  const showLoader = (): void => {
    if (showLoaderTimeoutRef.current !== null) {
      clearTimeout(showLoaderTimeoutRef.current);
      showLoaderTimeoutRef.current = null;
    }
    if (isOfflinePackage) {
      showLoaderTimeoutRef.current = setTimeout(() => {
        showLoaderTimeoutRef.current = null;
        setIsLoading(true);
      }, 500);
    }
  };

  const hideLoader = (): void => {
    if (showLoaderTimeoutRef.current !== null) {
      clearTimeout(showLoaderTimeoutRef.current);
      showLoaderTimeoutRef.current = null;
    }
    setIsLoading(false);
  };

  /**
   * todo: clean up this code correct error handling
   */
  const init = () => {
    if (isOfflinePackage && !usesHostedPiAuth) {
      loadPage(urlParams.userId);
    } else {
      const isIESDisabled = urlParams.disableIES;

      if (isIESDisabled && !(isOfflinePackage && usesHostedPiAuth)) {
        loadPage(urlParams.userId);
      } else {
        const sessionJsUrl = getSessionJsURL();
        if (isLocalhost()) {
          // no PI session on a localhost name; session.js is still loaded for window.piSession
          loadSessionJs(sessionJsUrl).then(
            () => {
              setMessage("Loading...");
              loadPage("No pi session");
            },
            (e: Error) => console.error("Dev Shell: Session js initialize error.", e.message),
          );
          return;
        }
        requirePiLogin({
          sessionJsUrl,
          clientId: getClientId(),
          cookieGateway: urlParams.securePackageAccess ? getDevShellExpressGatewayBaseUrl() : null,
          onStatus: setMessage,
        }).then(
          ({ token, userId }) => {
            if (usesHostedPiAuth) {
              piTokenRef.current = token;
            }
            setMessage("Loading...");
            loadPage(userId);
          },
          (e: Error) => console.error("Dev Shell: Session js initialize error.", e.message),
        );
      }
    }
  };

  useEffect(() => {
    init();

    // Following click and blur listeners are to close the Annotation Popup.
    window.addEventListener("click", (event: MouseEvent) => {
      const annotationPopup = document.querySelector(".annotation-popup");
      if (annotationPopup && !annotationPopup.contains(event.target as HTMLElement)) {
        hideAnnotationMenu();
      }

      const annotationsPopover = document.querySelector(".annotation-icons-popup");
      if (annotationsPopover && !annotationsPopover.contains(event.target as HTMLElement)) {
        hideAnnotationsPopover();
      }
    });
  }, []);

  useEffect(() => {
    const setOnline = (): void => {
      //console.log("DEV_SHELL - network status changed to online");
      viewerApi.onNetworkStatusChange(true);
    };
    const setOffline = (): void => {
      //console.log("DEV_SHELL - network status changed to offline");
      viewerApi.onNetworkStatusChange(false);
    };

    window.addEventListener("online", setOnline);
    window.addEventListener("offline", setOffline);

    return (): void => {
      window.removeEventListener("online", setOnline);
      window.removeEventListener("offline", setOffline);
    };
  }, []);

  useEffect(() => {
    getPrevNextItem(screensState, renderedId);
  }, [screensState, renderedId]);

  useEffect(() => {
    if (renderedId) {
      syncScreenIdUrlParam(renderedId);
    }
  }, [renderedId]);

  useEffect(() => {
    // Probing before the CloudFront cookies are primed 403s and hides the audio icon until a reload
    if (!packagePath || !urlParams.exportId || !packageAccessReady) {
      return;
    }
    let cancelled = false;
    probeLocalCourseAudio(packagePath).then(({ enabled, failedScreenIds: failedIds }) => {
      if (cancelled) {
        return;
      }
      setLocalCourseAudioEnabled(enabled);
      setFailedScreenIds(failedIds);
    });
    return () => {
      cancelled = true;
    };
  }, [packagePath, urlParams.exportId, packageAccessReady]);

  useEffect(() => {
    if (renderer !== RendererTypes.EXPRESS || !packagePath) {
      return;
    }
    let cancelled = false;
    isOldCourseForExpress(packagePath).then((isOld) => {
      if (!cancelled && isOld) {
        setPackageWarning(OLD_COURSE_EXPRESS_MESSAGE);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [renderer, packagePath]);

  const hideAnnotationMenu = (): void => {
    setShowAnnotationMenu(false);
  };

  const handleStartAudioFromHere = (): void => {
    const a = currentAnnotation;
    const dataElementId = a?.dataElementId;
    const textOffset = a?.selectedTextOffset;
    if (!dataElementId || typeof textOffset !== "number") {
      return;
    }

    // Session already active and assets already loaded -> seek immediately.
    if (navigationBarRef.current?.playTtsFromTextOffset(dataElementId, textOffset)) {
      hideAnnotationMenu();
      viewerApi?.annotations()?.clearSelection();
      return;
    }

    // No session yet -> activate one for the current screen before queuing the seek.
    if (!spotlightAudio) {
      if (!localCourseAudioEnabled || !packagePath) {
        return;
      }
      if (isLessonAudioFailed(idToRender, failedScreenIds)) {
        alert(NO_AUDIO_FOR_SCREEN_MESSAGE);
        return;
      }

      flushSync(() => {
        setSpotlightAudio(buildLocalSpotlightAudio(packagePath));
      });
      navigationBarRef.current?.armPlaybackFromUserGesture();
    }

    // Either a session was just activated above, or one was already active but its
    // assets are still loading -> queue the seek for whenever it's ready.
    navigationBarRef.current?.seekToTextOffsetOnceReady(dataElementId, textOffset);
    hideAnnotationMenu();
    viewerApi?.annotations()?.clearSelection();
  };

  const hideAnnotationsPopover = (): void => {
    setShowAnnotationsPopover(false);
  };

  const onAnnotationResponse = (data: IAnnotationData): void => {
    const currentScreenData = screensState.find(
      (screen: Screen) => screen.elementId === currentRenderedId,
    );

    data.screenTitle = getTitle(currentScreenData?.configurationFields || []);
    data.pageId = currentRenderedId;

    // Flashcards are companions to highlights/notes. Never convert the source
    // annotation in place — mint a new id and Add so the notebook entry remains.
    if (data.annotationType === AnnotationTypes.FlashCard && data.annotationId) {
      const existing = (DataProvider.getAnnotations(currentRenderedId) as IAnnotationData[])?.find(
        (annotation) => annotation.annotationId === data.annotationId,
      );

      if (existing && existing.annotationType !== AnnotationTypes.FlashCard) {
        data = {
          ...data,
          annotationId: getUniqueIdForAnnotation(),
          actionType: AnnotationActions.Add,
        };
        delete data.color;
        delete data.noteText;
      }
    }

    const actionType = data.actionType;

    DataProvider.saveAnnotation(data);
    setCurrentAnnotation(data);

    switch (actionType) {
      case AnnotationActions.Add:
        viewerApi.annotations().add(data);
        break;
      case AnnotationActions.Edit:
        viewerApi.annotations().edit(data);
        break;
      case AnnotationActions.Delete:
        viewerApi.annotations().delete(data);
        break;
    }

    setAnnotationUpdated((annotationUpdated) => !annotationUpdated);
  };

  /**
   * method to find the last "ACTIVITY" element's elementId in the ancestry because that is the root activity.
   */
  const fetchParentActivityIdFromRoute = (routeData: Route): void => {
    if (routeData.ancestry) {
      // Clone and reverse the ancestry array to find the last matching item
      const reversedAncestry = [...routeData.ancestry].reverse();
      const activity = reversedAncestry.find((item: Ancestry) => item.elementType === "ACTIVITY");
      if (activity) {
        setParentActivityId(activity.elementId);
      }
    }
  };

  const loadPage = (pearsonUid: string): void => {
    setPackageAccessReady(true);
    const { cohortId, deploymentId } = urlParams;
    if (deploymentId && cohortId) {
      const viewerConfig: ViewerConfig = {
        domElement: frameElement.current,
        cohortId,
        deploymentId,
        getAuthToken: usesHostedPiAuth
          ? (callback) => {
              // ?token= fallback: localhost never initializes piSession, so headless
              // tests inject a real PIE token via the URL (see comparator BV_PI_TOKEN)
              callback(piTokenRef.current || urlParams.token || "pi token");
            }
          : getAuthToken,
        packagePath,
        renderer,
        ...(isDeviceTypeToggleEnabled && deviceType !== DEFAULT_DEV_SHELL_DEVICE_TYPE
          ? { deviceType }
          : {}),
        pearsonUid,
        env: urlParams.env as ENVIRONMENTS,
        decrypt: (_screenId: string, _filesList: string[], onComplete: () => void) => {
          onComplete();
        },
        isOnline: () => {
          return window.navigator.onLine;
        },
        events: {
          onReady: async () => {
            console.log("App: onReady getCoursewareStructure", viewerApi);

            // Resolved here rather than in a mount effect: for a hosted secure package the
            // CloudFront signed cookies are only primed just before loadPage, so an earlier
            // request would 403. Not awaited - the title bar fills in when it lands.
            getShellPackageDates(packagePath ?? "", urlParams.exportId).then(setDates);

            if (packagePath) {
              const pageNumbers = window?.pageNumbers ?? (await getPageNumbers(packagePath));
              pageNumbersMapRef.current = pageNumbers;
            }

            let initialRoute: InitialRoute = { routeId: "" };
            try {
              const structure = (await getCoursewareStructure(viewerApi, isOfflinePackage))();
              setTocNodeHtml(structure.tocHtmlNode);
              setScreensState(structure.screens);
              initialRoute = resolveInitialRoute(structure, urlParams.screenId);
            } catch (e) {
              console.error("App: getCoursewareStructure failed", e);
              setPackageWarning("This is an unsupported package that does not have toc.json");
              return;
            }

            if (isOfflinePackage) {
              setMessage(getOfflineShellMessage());
            } else {
              setMessage("BV Web");
            }

            // Apply theme before first route so Express has CSS on the first paint (slow networks
            // otherwise paint once without theme, then updateTheme wins after async delay).
            try {
              const fetchedThemeVariants = await viewerApi.getThemeVariants();
              if (Array.isArray(fetchedThemeVariants)) {
                setThemeVariants(fetchedThemeVariants);

                const defaultThemeVariant = fetchedThemeVariants?.find(
                  (themeVariant) => themeVariant.state === "DEFAULT",
                );
                if (defaultThemeVariant) {
                  DefaultThemeOptions.variantId = defaultThemeVariant.variantId;
                }
                await viewerApi.updateTheme(DefaultThemeOptions);
              }
            } catch (e) {
              console.warn("App: onReady theme initialization failed", e);
            }

            if (initialRoute.warning) {
              setPackageWarning(initialRoute.warning);
            }
            viewerApi.route(initialRoute.routeId, initialRoute.subTarget);
          },
          onRouteChange: (data: Route) => {
            console.log("DEV_SHELL: onRouteChange", data);
            setRouteCompleted(false);

            if (!isOfflinePackage) {
              fetchParentActivityIdFromRoute(data);
            }
            //console.log("Dev Shell: "+  new Date().toISOString().substring(11,23) + " onRouteChange =>", data);
            /* TODO: replace currentRenderedId with renderedId from state.
             Currently using non state local variable "currentRenderedId" because renderedId is coming blank on each route change call.
             */
            if (data?.id !== currentRenderedId) {
              if (isOfflinePackage) {
                closeTestEtextWidget(currentRenderedId);
              }
              hideAnnotationMenu();

              /**
               * In mobile, we do swipe through multiple screens and those are preloaded, so there is no need to show loader.
               */
              if (renderer !== RendererTypes.MOBILE) {
                showLoader();
              }

              currentRenderedId = data?.id;
              setStitchedLessonRenderId("");
              setRenderedId(currentRenderedId);
            }

            //for BVExpress we get annotations on onRouteComplete
            //that avoids any race conditions
            if (renderer !== RendererTypes.EXPRESS && isOfflinePackage) {
              annotationData = DataProvider.getAnnotations(data?.id) as IAnnotationData[];
              viewerApi.annotations().load(annotationData);
            }
          },
          onRouteComplete: (data: Route) => {
            //console.log("Dev Shell:"+  new Date().toISOString().substring(11,23) + " onRouteComplete =>", data);
            setRouteCompleted(true);
            hideLoader();
            //for BVExpress we get annotations on onRouteComplete
            //that avoids any race conditions
            console.log("DEV_SHELL: onRouteComplete renderer", renderer);
            if (renderer === RendererTypes.EXPRESS && isOfflinePackage) {
              annotationData = DataProvider.getAnnotations(data?.id) as IAnnotationData[];
              viewerApi.annotations().load(annotationData);
            }
          },
          onLinkClick: (data: Link) => {
            if (data.type === "ExternalLink") {
              if (isAndroid) {
                alert("An external link is clicked");
              }
              console.log("An external link is clicked: ", data);
            }
          },
          onPageScroll: (data: ScrollData) => {
            //console.log("Dev Shell: onPageScroll =>", data);
            hideAnnotationsPopover();
            if (data.activeScreenId) {
              setStitchedLessonRenderId(data.activeScreenId);
            }
            if (data.dataId) {
              // TODO; persist the dataId to scroll back to this location
            }
          },
          onPageChange: (data: PageData) => {
            setPageNumber(data.pageNumber);
            //console.log("Dev Shell: "+ Date.now() + " onPageChange =>", data);
          },
          onRequestScreenPageNumbers: (elementId: string) => {
            return pageNumbersMapRef.current[elementId] ?? [];
          },
          onMediaClick: (data: Media) => {
            if (data.mediaType === "Image") {
              setImageData(data.metadata as Image);
              return;
            }

            // Following will only execute in Mobile in case of Audio and Video.
            console.log(`${data.mediaType} clicked: `, data);
            if (isAndroid) {
              alert(`${data.mediaType} clicked`);
            }
          },
          /** onMediaPlay callback will be triggered from BV when user plays any video or audio in web
           * and can be used to perform any necessary actions if needed by the consumer application
           * one such use case would be when starting TTS by consumer app outside BV screen.
           * @param {Media} data - data from the played media which includes
           * mediaType (audio or video) and other metadata (url, transcript, duration, title etc ) )
           */
          onMediaPlay: (data: Media) => {
            console.log(`${data.mediaType} played: `, data);
          },

          onAnnotationRequest: (annotation: IAnnotationData): void => {
            console.log("onAnnotationRequest: annotation request data", annotation);
            hideAnnotationsPopover();
            if (!annotation.selectedText) {
              /**
               * This is the case of when user clicks on an existing annotation.
               * It's fetching annotation data from the client.
               */
              const savedAnnotation = (
                DataProvider.getAnnotations(currentRenderedId) as IAnnotationData[]
              )?.find((item: IAnnotationData) => item.annotationId === annotation.annotationId);

              if (savedAnnotation) {
                savedAnnotation.mousePosition = annotation.mousePosition;
                annotation = savedAnnotation;
              }
            }

            setCurrentAnnotation(annotation);
            setShowAnnotationMenu(true);
          },
          onAnnotationIconClick: (data: IAnnotationPopoverData): void => {
            //console.log("Dev Shell: onAnnotationIconClick =>", data);

            if (data?.annotations?.length === 1) {
              const singleAnnotation = data.annotations[0];
              if (!singleAnnotation) {
                return;
              }
              // [TODO] Instead of annotations[0], fetch from DataProvider.getAnnotationById(annotationId)
              setCurrentAnnotation(singleAnnotation);
              if (singleAnnotation.noteText) {
                setNotesPopupDisplay(true);
              } else {
                setFlashcardPopupDisplay(true);
              }
            }
            if (data?.annotations?.length > 1) {
              setShowAnnotationsPopover(true);
              setAnnotationsPopoverData(data);
            }
          },
          onPageClick: (): void => {
            // console.log("Dev Shell: onPageClick.");

            hideAnnotationMenu();
            hideAnnotationsPopover();
            setNotesPopupDisplay(false);
            setFlashcardPopupDisplay(false);
          },
        },
      };

      if (renderer === RendererTypes.EXPRESS) {
        viewerApi = new ViewerExpressLegacyAdapter(viewerConfig);
        //there is nothing to wait for in express
        viewerConfig.events?.onReady?.();
      } else {
        viewerApi = new ViewerApi(viewerConfig);
      }
      window.__viewerApi = viewerApi;
    }
  };

  const closeModal = (): void => {
    setImageData(null);
  };
  const idToRender = stitchedLessonRenderId || renderedId;
  const spotlightAudioDisabled = isLessonAudioFailed(idToRender, failedScreenIds);

  const handleSpotlightAudioToggle = (): void => {
    if (!localCourseAudioEnabled || !packagePath) {
      return;
    }

    if (spotlightAudio?.localAudioBaseUrl) {
      flushSync(() => {
        setSpotlightAudio(undefined);
      });
      setAudioPlayerVisible(false);
      console.log("Dev Shell spotlight audio: deactivated local spotlight");
      return;
    }

    if (isLessonAudioFailed(idToRender, failedScreenIds)) {
      alert(NO_AUDIO_FOR_SCREEN_MESSAGE);
      return;
    }
    flushSync(() => {
      setSpotlightAudio(buildLocalSpotlightAudio(packagePath));
    });
    navigationBarRef.current?.armPlaybackFromUserGesture();
    navigationBarRef.current?.startSpotlightAudioForCurrentScreen();
    console.log("Dev Shell spotlight audio: activated local spotlight", {
      packagePath,
      localAudioBaseUrl: getLocalAudioBaseUrl(packagePath),
      lessonId: idToRender,
    });
  };

  const handleLoadTestWidget = (config: EtextWidgetTestConfig): void => {
    void loadTestEtextWidget(idToRender, renderer, config);
  };

  const showStartAudioHere =
    Boolean(
      spotlightAudio?.ttsURL || spotlightAudio?.localAudioBaseUrl || localCourseAudioEnabled,
    ) &&
    typeof currentAnnotation?.selectedTextOffset === "number" &&
    Boolean(currentAnnotation?.dataElementId);

  return (
    <ThemeContext.Provider value={{ currentTheme, setCurrentTheme }}>
      <AppHeader
        app={isExpressBuild ? "express" : "bv"}
        uat={isUatBuild}
        title={message}
        dates={dates}
        warning={packageWarning}
        exportId={urlParams.exportId}
        screenId={renderedId}
      />
      <div className="app-shell">
        {urlParams.deploymentId && urlParams.cohortId ? (
          <>
            <EnvironmentForm autoOpen={false} />
            <div className="content-wrapper">
              <AsideBar
                annotationUpdated={annotationUpdated}
                tocNodeHtml={tocNodeHtml}
                viewerApi={viewerApi}
                renderedId={idToRender}
                parentActivityId={parentActivityId}
                onSpotlightClick={setSpotlightAudio}
                onAudioPlayerToggle={handleSpotlightAudioToggle}
                showSpotlightAudio={localCourseAudioEnabled}
                spotlightAudioDisabled={spotlightAudioDisabled}
                hideAudioSettings={localCourseAudioEnabled}
                minimalShell={urlParams.minimalShell}
                showDeviceTypeToggle={isDeviceTypeToggleEnabled}
                deviceType={deviceType}
                onDeviceTypeChange={handleDeviceTypeChange}
                packagePath={packagePath}
                showTestWidgetButton={isOfflinePackage && !isUatBuild}
                onLoadTestWidget={handleLoadTestWidget}
              />

              <div
                className={`content ${renderer === RendererTypes.EXPRESS ? "content-express" : ""}`}
              >
                <div className="content-frame-container">
                  <div className={`page-loader ${isLoading ? "show" : ""}`}>
                    <div className="spinner"></div>
                  </div>

                  <div
                    className="content-frame"
                    data-disable-check-for-top-level-plugin="true"
                    ref={frameElement}
                  ></div>
                </div>
                <NavigationBar
                  ref={navigationBarRef}
                  viewerApi={viewerApi}
                  prevItem={prevItem}
                  nextItem={nextItem}
                  currentItem={renderedId}
                  pageNumber={pageNumber}
                  pageNumbersMap={pageNumbersMapRef.current}
                  renderedId={idToRender}
                  routeCompleted={routeCompleted}
                  spotlightAudio={spotlightAudio}
                  failedScreenIds={failedScreenIds}
                  shouldNavigateByPage={isOfflinePackage}
                  isAudioPlayerVisible={isAudioPlayerVisible}
                  setAudioPlayerVisible={setAudioPlayerVisible}
                ></NavigationBar>
              </div>
            </div>
          </>
        ) : (
          <EnvironmentForm autoOpen={true} />
        )}
      </div>

      {imageData && (
        <Fragment>
          <div className="modal-backdrop" onClick={closeModal}></div>
          <div className="modal-content">
            <button className="close-modal" onClick={closeModal}></button>
            <img alt={imageData.alt} className="zoomable-image" src={imageData.src} />
            {imageData.caption && (
              <div
                className="caption"
                dangerouslySetInnerHTML={{ __html: imageData.caption }}
              ></div>
            )}
          </div>
        </Fragment>
      )}

      <Annotations
        annotation={currentAnnotation}
        setCurrentAnnotation={setCurrentAnnotation}
        onAnnotationResponse={onAnnotationResponse}
        notesPopupDisplay={notesPopupDisplay}
        setNotesPopupDisplay={setNotesPopupDisplay}
        flashcardPopupDisplay={flashcardPopupDisplay}
        setFlashcardPopupDisplay={setFlashcardPopupDisplay}
        showAnnotationMenu={showAnnotationMenu}
        hideAnnotationMenu={hideAnnotationMenu}
        annotationsPopoverData={annotationsPopoverData}
        showAnnotationsPopover={showAnnotationsPopover}
        hideAnnotationsPopover={hideAnnotationsPopover}
        renderer={renderer}
        showStartAudioHere={showStartAudioHere}
        onStartAudioHere={handleStartAudioFromHere}
      ></Annotations>
    </ThemeContext.Provider>
  );
};

export default App;
